<template>
  <VaLayout
    :top="{ fixed: true, order: 2 }"
    :left="
      isOnboardingFlow
        ? false
        : { fixed: true, absolute: breakpoints.mdDown, order: 1, overlay: breakpoints.mdDown && !isSidebarMinimized }
    "
    @leftOverlayClick="isSidebarMinimized = true"
  >
    <template #top>
      <AppNavbar :is-mobile="isMobile" />
    </template>

    <template v-if="!isOnboardingFlow" #left>
      <AppSidebar :minimized="isSidebarMinimized" :animated="!isMobile" :mobile="isMobile" />
    </template>

    <template #content>
      <div v-if="!isOnboardingFlow" :class="{ minimized: isSidebarMinimized }" class="app-layout__sidebar-wrapper">
        <div v-if="isFullScreenSidebar" class="flex justify-end">
          <VaButton class="px-4 py-4" icon="md_close" preset="plain" @click="onCloseSidebarButtonClick" />
        </div>
      </div>
      <TeamModeBanner />
      <StripeVerificationBanner />
      <main class="px-4 pb-4 pt-[30px]" :class="mobileMainPaddingClass">
        <article>
          <RouterView />
        </article>
      </main>
      <div
        v-if="!isOnboardingFlow"
        class="mobile-bottom-nav"
        :class="{ 'mobile-bottom-nav--hidden': !isBottomMenuVisible }"
      >
        <nav
          class="mobile-bottom-menu"
          :class="{
            'mobile-bottom-menu--no-active': activeBottomMenuIndex < 0,
          }"
          :style="{ '--menu-index': activeBottomMenuIndex, '--menu-count': bottomMenuCount }"
          :aria-label="isTeamMode ? 'Team navigation' : 'Mobile navigation'"
        >
          <div class="mobile-bottom-menu__indicator" aria-hidden="true"></div>
          <template v-if="isTeamMode">
            <button
              type="button"
              class="mobile-bottom-menu__item"
              :class="{ 'mobile-bottom-menu__item--active': isActiveRoute('team-dashboard') }"
              @click="goTo('team-dashboard')"
            >
              <VaIcon
                aria-hidden="true"
                name="vuestic-iconset-dashboard"
                size="14px"
                class="mobile-bottom-menu__icon material-symbols-outlined"
              />
              <span class="mobile-bottom-menu__label">{{ t('menu.team-dashboard') }}</span>
            </button>
            <button
              type="button"
              class="mobile-bottom-menu__item"
              :class="{ 'mobile-bottom-menu__item--active': isActiveRoute('team-invite') }"
              @click="goTo('team-invite')"
            >
              <VaIcon
                aria-hidden="true"
                name="groups"
                size="16px"
                class="mobile-bottom-menu__icon material-symbols-outlined"
              />
              <span class="mobile-bottom-menu__label">{{ t('menu.team-invite') }}</span>
            </button>
            <button
              type="button"
              class="mobile-bottom-menu__item"
              :class="{ 'mobile-bottom-menu__item--active': isActiveRoute('team-shifts') }"
              @click="goTo('team-shifts')"
            >
              <VaIcon
                aria-hidden="true"
                name="schedule"
                size="16px"
                class="mobile-bottom-menu__icon material-symbols-outlined"
              />
              <span class="mobile-bottom-menu__label">{{ t('menu.team-shifts') }}</span>
            </button>
            <button
              type="button"
              class="mobile-bottom-menu__item"
              :class="{ 'mobile-bottom-menu__item--active': isActiveRoute('team-qr') }"
              @click="goTo('team-qr')"
            >
              <VaIcon
                aria-hidden="true"
                name="qr_code"
                size="16px"
                class="mobile-bottom-menu__icon material-symbols-outlined"
              />
              <span class="mobile-bottom-menu__label">{{ t('menu.team-qr') }}</span>
            </button>
            <button
              type="button"
              class="mobile-bottom-menu__item"
              :class="{ 'mobile-bottom-menu__item--active': isActiveRoute('team-profile') }"
              @click="goTo('team-profile')"
            >
              <VaIcon
                aria-hidden="true"
                name="settings"
                size="16px"
                class="mobile-bottom-menu__icon material-symbols-outlined"
              />
              <span class="mobile-bottom-menu__label">{{ t('menu.team-profile') }}</span>
            </button>
          </template>
          <template v-else>
            <button
              type="button"
              class="mobile-bottom-menu__item"
              :class="{ 'mobile-bottom-menu__item--active': isActiveRoute('dashboard') }"
              @click="goTo('dashboard')"
            >
              <VaIcon
                aria-hidden="true"
                name="vuestic-iconset-dashboard"
                size="14px"
                class="mobile-bottom-menu__icon material-symbols-outlined"
              />
              <span class="mobile-bottom-menu__label">Dashboard</span>
            </button>
            <button
              type="button"
              class="mobile-bottom-menu__item"
              :class="{ 'mobile-bottom-menu__item--active': isActiveRoute('qr-code') }"
              @click="goTo('qr-code')"
            >
              <VaIcon
                aria-hidden="true"
                name="qr_code"
                size="16px"
                class="mobile-bottom-menu__icon material-symbols-outlined"
              />
              <span class="mobile-bottom-menu__label">QR</span>
            </button>
            <button
              type="button"
              class="mobile-bottom-menu__item"
              :class="{ 'mobile-bottom-menu__item--active': isActiveRoute('teams') }"
              @click="goTo('teams')"
            >
              <VaIcon
                aria-hidden="true"
                name="groups"
                size="16px"
                class="mobile-bottom-menu__icon material-symbols-outlined"
              />
              <span class="mobile-bottom-menu__label">{{ t('menu.teams') }}</span>
            </button>
            <button
              type="button"
              class="mobile-bottom-menu__item"
              :class="{ 'mobile-bottom-menu__item--active': isActiveRoute('payouts') }"
              @click="goTo('payouts')"
            >
              <VaIcon
                aria-hidden="true"
                name="credit_card"
                size="16px"
                class="mobile-bottom-menu__icon material-symbols-outlined"
              />
              <span class="mobile-bottom-menu__label">Payouts</span>
            </button>
            <button
              type="button"
              class="mobile-bottom-menu__item"
              :class="{ 'mobile-bottom-menu__item--active': isActiveRoute('preferences') }"
              @click="goTo('preferences')"
            >
              <VaIcon
                aria-hidden="true"
                name="account_circle"
                size="16px"
                class="mobile-bottom-menu__icon material-symbols-outlined"
              />
              <span class="mobile-bottom-menu__label">Profile</span>
            </button>
          </template>
        </nav>
      </div>
    </template>
  </VaLayout>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'
import { useBreakpoint } from 'vuestic-ui'
import { useI18n } from 'vue-i18n'

import { useGlobalStore } from '../stores/global-store'

import AppNavbar from '../components/navbar/AppNavbar.vue'
import AppSidebar from '../components/sidebar/AppSidebar.vue'
import TeamModeBanner from '../components/navbar/TeamModeBanner.vue'
import StripeVerificationBanner from '../components/navbar/StripeVerificationBanner.vue'

const GlobalStore = useGlobalStore()
const { t } = useI18n()

const breakpoints = useBreakpoint()
const route = useRoute()
const router = useRouter()

const sidebarWidth = ref('16rem')
const sidebarMinimizedWidth = ref(undefined)

const isMobile = ref(false)
const isTablet = ref(false)
const { isSidebarMinimized } = storeToRefs(GlobalStore)
const isBottomMenuVisible = ref(true)
const lastScrollY = ref(0)

const onResize = () => {
  isSidebarMinimized.value = breakpoints.mdDown
  isMobile.value = breakpoints.smDown
  isTablet.value = breakpoints.mdDown
  sidebarMinimizedWidth.value = isMobile.value ? '0' : '4.5rem'
  sidebarWidth.value = isTablet.value ? '100%' : '16rem'
}

const onScroll = () => {
  const currentY = window.scrollY || 0
  const delta = currentY - lastScrollY.value

  // Always show near top.
  if (currentY <= 12) {
    isBottomMenuVisible.value = true
    lastScrollY.value = currentY
    return
  }

  // Hide on scroll down, show on scroll up.
  if (delta > 4) {
    isBottomMenuVisible.value = false
  } else if (delta < -4) {
    isBottomMenuVisible.value = true
  }

  lastScrollY.value = currentY
}

onMounted(() => {
  window.addEventListener('resize', onResize)
  window.addEventListener('scroll', onScroll, { passive: true })
  onResize()
  lastScrollY.value = window.scrollY || 0
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
  window.removeEventListener('scroll', onScroll)
})

onBeforeRouteUpdate(() => {
  if (breakpoints.mdDown) {
    // Collapse sidebar after route change for Mobile
    isSidebarMinimized.value = true
  }
})

const isFullScreenSidebar = computed(() => isTablet.value && !isSidebarMinimized.value)

const onCloseSidebarButtonClick = () => {
  isSidebarMinimized.value = true
}

const isOnboardingFlow = computed(() => route.matched.some((r) => r.meta.isOnboardingFlow))

/** Matches sidebar logic: inside /teams/:teamId/… → team nav on mobile. */
const isTeamMode = computed(() => {
  if (route.meta?.teamContext === 'inside') return true
  return !!route.params?.teamId
})

const mobileMainPaddingClass = computed(() => (isTeamMode.value ? 'max-[600px]:pb-40' : 'max-[600px]:pb-24'))

const bottomMenuCount = computed(() => 5)

const isActiveRoute = (name) => route.name === name

const goTo = (name) => {
  if (route.name === name) return
  const teamRoutes = ['team-dashboard', 'team-invite', 'team-shifts', 'team-qr', 'team-profile']
  if (teamRoutes.includes(name)) {
    const tid = route.params.teamId
    if (!tid) return
    router.push({
      name,
      params: { teamId: String(Array.isArray(tid) ? tid[0] : tid) },
    })
    return
  }
  router.push({ name })
}

const TEAM_BOTTOM_ORDER = ['team-dashboard', 'team-invite', 'team-shifts', 'team-qr', 'team-profile']

const activeBottomMenuIndex = computed(() => {
  const name = String(route.name || '')
  if (isTeamMode.value) {
    const idx = TEAM_BOTTOM_ORDER.indexOf(name)
    return idx
  }
  if (name === 'dashboard') return 0
  if (name === 'qr-code') return 1
  if (name === 'teams') return 2
  if (name === 'payouts') return 3
  // Treat settings/profile-related pages as Profile tab.
  if (name === 'preferences' || name === 'settings') return 4
  return -1
})
</script>

<style lang="scss" scoped>
// Prevent icon jump on animation
.va-sidebar {
  width: unset !important;
  min-width: unset !important;
}
.va-layout {
  background-color: #f6f9fc;
}

.mobile-bottom-nav {
  position: fixed;
  left: 12px;
  right: 12px;
  bottom: max(10px, env(safe-area-inset-bottom));
  z-index: 1100;
  display: flex;
  flex-direction: column;
  width: calc(100% - 24px);
  opacity: 1;
  transition: opacity 0.22s ease;

  &__back {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    border: 0;
    border-radius: 18px 18px 0 0;
    background: #0073e6;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.2;
    height: 51px;
    padding: 10px 16px;
    cursor: pointer;
    display: flex;
    box-shadow: 0 6px 16px #0073e647;
    margin-bottom: -15px;
    transition: opacity 0.18s ease;
    align-items: flex-start;
    justify-content: center;

    &:active {
      opacity: 0.88;
    }
  }
}

.mobile-bottom-nav--hidden {
  opacity: 0;
  pointer-events: none;
}

.mobile-bottom-menu {
  position: relative;
  flex-shrink: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  overflow: hidden;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(12px) saturate(200%);
  -webkit-backdrop-filter: blur(14px) saturate(160%);
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.14);

  &__indicator {
    position: absolute;
    top: 0px;
    left: 0px;
    width: calc((100% - (var(--menu-count, 4) - 1) * 8px) / var(--menu-count, 4));
    height: calc(100% - 0px);
    border-radius: 16px;
    background: #0073e6;
    transform: translateX(calc(var(--menu-index) * (100% + 8px)));
    transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1);
    pointer-events: none;
  }

  &__item {
    flex: 1;
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    border: 0;
    text-align: center;
    text-decoration: none;
    background: transparent;
    color: #425466;
    font-size: 11px;
    font-weight: 700;
    line-height: 1.1;
    padding: 8px 4px;
    border-radius: 12px;
    transition:
      background-color 0.2s ease,
      color 0.2s ease;
  }

  &__icon {
    flex-shrink: 0;
    color: currentColor;
  }

  &__item--active {
    color: #fff;
  }
}

.mobile-bottom-menu--no-active .mobile-bottom-menu__indicator {
  opacity: 0;
}

@media (min-width: 601px) {
  .mobile-bottom-nav {
    display: none;
  }
}
</style>
